import { useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useRouter } from "@tanstack/react-router";
import { PATHS } from "@/config/paths";
import { EMAIL, WHATSAPP_COMMUNITY_URL } from "@/config/constants";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useTranslation } from "@/hooks/useTranslation";
import { Heading } from "@/components/ui/heading";

// FAQ IDs for translation keys
const FAQ_IDS = [
  "d1",
  "d2",
  "d3",
  "d4",
  "d5",
  "d6", // Discovery
  "f1",
  "f2",
  "f3",
  "f4",
  "f5",
  "f6",
  "f7",
  "f8",
  "f9", // Features
  "p1",
  "p2",
  "p3",
  "p4",
  "p5", // Personalization
  "b1",
  "b2",
  "b3",
  "b4",
  "b5", // Benefits
  "e1",
  "e2",
  "e3",
  "e4",
  "e5", // Ease of Use
  "u1",
  "u2",
  "u3",
  "u4",
  "u5", // Pain Points
];

const FAQ_SECTIONS = [
  { key: "discovery", labelKey: "faq.sections.discovery" },
  { key: "features", labelKey: "faq.sections.features" },
  { key: "personalization", labelKey: "faq.sections.personalization" },
  { key: "benefits", labelKey: "faq.sections.benefits" },
  { key: "ease", labelKey: "faq.sections.ease" },
  { key: "pain", labelKey: "faq.sections.pain" },
];

const FAQ_SECTION_MAP = {
  discovery: ["d1", "d2", "d3", "d4", "d5", "d6"],
  features: ["f1", "f2", "f3", "f4", "f5", "f6", "f7", "f8", "f9"],
  personalization: ["p1", "p2", "p3", "p4", "p5"],
  benefits: ["b1", "b2", "b3", "b4", "b5"],
  ease: ["e1", "e2", "e3", "e4", "e5"],
  pain: ["u1", "u2", "u3", "u4", "u5"],
};

export function ProgressFAQ() {
  const router = useRouter();
  const { t, tNode } = useTranslation();
  const [section, setSection] = useState<keyof typeof FAQ_SECTION_MAP>("discovery");

  const onContactClick = () => {
    router.navigate({ to: PATHS.CONTACT });
  };

  // Generate FAQ data from translation keys
  const faqs = FAQ_IDS.map((id) => ({
    id,
    question: t(`faq.${id}.question`),
    answer: t(`faq.${id}.answer`),
  }));

  const filteredFaqs = faqs.filter((faq) => FAQ_SECTION_MAP[section].includes(faq.id));

  return (
    <section id="faq" data-testid="faq" className="col-span-12 grid grid-cols-12 md:py-8">
      {/* Hidden div with all FAQs for SEO - not visible to users but crawlable by search engines */}
      <div className="hidden">
        {FAQ_IDS.map((id) => (
          <div key={`seo-${id}`}>
            <Heading level="h3" size="h6">
              {t(`faq.${id}.question`)}
            </Heading>
            <div>{t(`faq.${id}.answer`)}</div>
          </div>
        ))}
      </div>

      <div className="col-span-12 grid grid-cols-12 gap-8">
        {/* Left column - Sticky */}
        <div className="col-span-12 @[768px]:col-span-4 @[768px]:sticky @[768px]:top-20 @[768px]:self-start">
          <Heading level="h2" size="h3" data-testid="faq-title">
            {t("faq.title")}
          </Heading>
          <p className="text-muted-foreground mb-8">{t("faq.description")}</p>

          <div className="space-y-6">
            <div>
              <Heading level="h3" size="h6" className="font-medium mb-2">
                {t("faq.have_another_question")}
              </Heading>
              <p className="text-sm text-muted-foreground mb-4">
                {tNode("faq.contact_description", {
                  whatsappCommunityUrl: (
                    <a
                      href={WHATSAPP_COMMUNITY_URL}
                      className="hover:underline font-medium"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      WhatsApp
                    </a>
                  ),
                  email: (
                    <a href={`mailto:${EMAIL}`} className="hover:underline font-medium">
                      {EMAIL}
                    </a>
                  ),
                })}
              </p>
              <Button
                data-umami-event="progress-faq-contact-us"
                variant="outline"
                onClick={onContactClick}
              >
                {t("faq.contact_button")}
              </Button>
            </div>
          </div>
        </div>

        {/* Right column - ToggleGroup + Accordion */}
        <div className="col-span-12 @[768px]:col-span-8">
          <ToggleGroup
            type="single"
            value={section}
            onValueChange={(val) => val && setSection(val as keyof typeof FAQ_SECTION_MAP)}
            className="mb-6 flex flex-wrap"
          >
            {FAQ_SECTIONS.map((s) => (
              <ToggleGroupItem key={s.key} value={s.key} className="px-4 py-3">
                {t(s.labelKey)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <Accordion type="single" collapsible className="w-full">
            {filteredFaqs.map((faq, index) => (
              <div key={faq.id}>
                <AccordionItem
                  value={`item-${faq.id}`}
                  className={cn(
                    "border-b",
                    index === 0 && "border-t",
                    "focus-within:relative focus-within:z-10",
                  )}
                >
                  <AccordionTrigger
                    data-umami-event={`progress-faq-${faq.id}`}
                    className={cn(
                      "items-center text-base transition-colors py-5",
                      "hover:text-primary",
                      "focus-visible:outline-hidden group",
                      "w-full",
                    )}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="text-left font-medium">{faq.question}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground transition-all duration-300">
                    <div className="pb-4 text-pretty text-lg">{faq.answer}</div>
                  </AccordionContent>
                </AccordionItem>
              </div>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
